import type { ReactNode } from 'react'
import styles from './Timeline.module.css'

/**
 * The record of what happened, in order.
 *
 * This renders `case_events` and `audit_events`, and both are append-only in
 * the database. The point of this file is that they are append-only on the
 * screen as well.
 *
 * There is nothing here to press. No row hover, no chevron, no pencil, no
 * overflow menu, no drag handle. That is a design constraint rather than an
 * unfinished component. A timeline that looks editable invites somebody to
 * correct an entry, and when the correction turns out to be impossible they
 * conclude the software is broken, rather than learning the thing the model is
 * trying to teach them: the timeline is not a description of the case, it is
 * what happened to it, and it is fixed. A mistake is corrected the way it is
 * corrected on paper, by adding an entry that says so.
 *
 * The events are also never sorted here. They arrive in the order the server
 * holds them and they are rendered in that order. Sorting by timestamp in the
 * browser looks harmless and quietly reorders any two entries recorded in the
 * same second, which on a protection case is the difference between an
 * escalation that followed a disclosure and one that preceded it.
 */

/**
 * The `case_events` vocabulary from the platform model, plus `system` for the
 * entries no person made. Kept as the model's own words so that a row can be
 * rendered from an API response without a translation table in between, which
 * is where a value like `status_change` quietly becomes `statusChange` and then
 * matches nothing.
 */
export type TimelineKind =
  | 'note'
  | 'status_change'
  | 'assignment'
  | 'referral'
  | 'contact'
  | 'consent'
  | 'escalation'
  | 'system'

export interface TimelineEvent {
  id: string
  kind?: TimelineKind
  /** Machine readable, for the `datetime` attribute. An ISO 8601 string. */
  at: string
  /** The same moment, written for a person. Formatted by the caller. */
  atLabel: string
  /** What happened. One line. */
  summary: ReactNode
  /** Who did it. Absent for anything the system did by itself. */
  actor?: string
  /** The note, the referral detail, the reason given. */
  body?: ReactNode
  /**
   * The kind, in the reader's language. Present, the kind is carried by a word
   * as well as by the colour of the marker, which is the design system's rule:
   * status is never colour alone.
   */
  kindLabel?: string
}

export interface TimelineProps {
  events: readonly TimelineEvent[]
  /** Names the list: "Case history", "Audit trail". */
  label: string
  className?: string
}

export function Timeline({ events, label, className }: TimelineProps) {
  return (
    <ol
      className={[styles.timeline, className].filter(Boolean).join(' ')}
      // `role="list"` alongside `list-style: none`, without which Safari drops
      // the list semantics and the reader loses both the count and their place.
      role="list"
      aria-label={label}
    >
      {events.map((event) => (
        <li key={event.id} className={styles.event} data-kind={event.kind ?? 'note'}>
          <span className={styles.marker} aria-hidden="true" />

          <div className={styles.content}>
            <p className={styles.head}>
              <time className={styles.time} dateTime={event.at}>
                {event.atLabel}
              </time>
              {event.kindLabel ? <span className={styles.kind}>{event.kindLabel}</span> : null}
              {event.actor ? <span className={styles.actor}>{event.actor}</span> : null}
            </p>

            <p className={styles.summary}>{event.summary}</p>

            {event.body ? <div className={styles.body}>{event.body}</div> : null}
          </div>
        </li>
      ))}
    </ol>
  )
}
